import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';

export interface AdminSubscription {
  id: string;
  user_id: string | null;
  plan_id: string | null;
  status: string;
  started_at: string | null;
  ends_at: string | null;
  profiles: {
    full_name: string | null;
    email: string | null;
  } | null;
}

export interface FreeUser {
  id: string;
  full_name: string | null;
  email: string | null;
  created_at: string;
}

export function useAdminSubscriptions() {
  const [subscriptions, setSubscriptions] = useState<AdminSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const [freeUsers, setFreeUsers] = useState<FreeUser[]>([]);
  const [freeUsersCount, setFreeUsersCount] = useState(0);
  const [freeUsersLoading, setFreeUsersLoading] = useState(true);

  const fetchSubscriptions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: subsData, error: subsError } = await supabase
        .from('user_subscriptions')
        .select(`
          id,
          user_id,
          plan_id,
          status,
          started_at,
          ends_at
        `)
        .eq('status', 'active')
        .order('started_at', { ascending: false });

      if (subsError) {
        toast({
          title: "Error fetching subscriptions",
          description: `Could not load subscription data. ${subsError.message}`,
          variant: "destructive",
        });
        throw subsError;
      }

      if (!subsData || subsData.length === 0) {
        setSubscriptions([]);
        setLoading(false);
        return;
      }

      const userIds = subsData
        .map(sub => sub.user_id)
        .filter((id): id is string => id !== null);

      if (userIds.length === 0) {
        const subsWithNoProfiles = subsData.map(sub => ({ ...sub, profiles: null }));
        setSubscriptions(subsWithNoProfiles as AdminSubscription[]);
        setLoading(false);
        return;
      }

      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, full_name, email')
        .in('id', userIds);

      if (profilesError) {
        toast({
          title: "Error fetching user profiles",
          description: `Could not load profile data for subscriptions. ${profilesError.message}`,
          variant: "destructive",
        });
        const subsWithNoProfiles = subsData.map(sub => ({ ...sub, profiles: null }));
        setSubscriptions(subsWithNoProfiles as AdminSubscription[]);
        throw profilesError;
      }

      const profilesMap = new Map(profilesData.map(p => [p.id, { full_name: p.full_name, email: p.email }]));
      
      const combinedData = subsData.map(sub => ({
        ...sub,
        profiles: sub.user_id ? profilesMap.get(sub.user_id) || null : null,
      }));

      setSubscriptions(combinedData as AdminSubscription[]);

    } catch (e) {
      setError(e as Error);
      console.error("Error fetching subscriptions data:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFreeUsers = useCallback(async (page: number, pageSize: number, searchTerm: string) => {
    setFreeUsersLoading(true);
    try {
      const { data: subbedUserIdsData, error: subbedUserIdsError } = await supabase
        .from('user_subscriptions')
        .select('user_id')
        .eq('status', 'active')
        .not('user_id', 'is', null);

      if (subbedUserIdsError) throw subbedUserIdsError;

      const subbedUserIds = subbedUserIdsData.map(s => s.user_id).filter((id): id is string => id !== null);

      let query = supabase
        .from('profiles')
        .select('id, full_name, email, created_at', { count: 'exact' });

      if (subbedUserIds.length > 0) {
        query = query.not('id', 'in', `(${subbedUserIds.join(',')})`);
      }

      if (searchTerm) {
        query = query.or(`full_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`);
      }

      const from = page * pageSize;
      const to = from + pageSize - 1;

      query = query.range(from, to).order('created_at', { ascending: false });

      const { data, error: queryError, count } = await query;

      if (queryError) throw queryError;

      setFreeUsers(data as FreeUser[]);
      setFreeUsersCount(count || 0);

    } catch (e) {
      setError(e as Error);
      console.error("Error fetching free users:", e);
      toast({
        title: "Error fetching free users",
        description: (e as Error).message,
        variant: "destructive",
      });
    } finally {
      setFreeUsersLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  return { 
    subscriptions, 
    loading, 
    error, 
    refresh: fetchSubscriptions,
    freeUsers,
    freeUsersCount,
    freeUsersLoading,
    fetchFreeUsers
  };
}